import { yupResolver } from '@hookform/resolvers/yup';
import type React from 'react';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { storeOrganizationInfo } from '@/api/SageAdmin/ClientAccount';
import Button from '@/components/UI/Button';
import { HookForm } from '@/components/UI/HookForm/Form';
import { HookInput } from '@/components/UI/HookForm/Form/Input';
import { HookSelect } from '@/components/UI/HookForm/Form/Select';
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
import ScheduleDemo from '../ScheduleDemo';
import SENTerminalSolutions from '../SENTerminalSolutions';
import styles from './OrganizationInfo.module.css';
import { validationSchema } from './validationSchema';

// Constants for form options
const organizationRoles = [
  { value: 'director', label: 'Director/Executive Leadership' },
  { value: 'program-manager', label: 'Program Manager' },
  { value: 'project-manager', label: 'Project Manager' },
  { value: 'financial', label: 'Financial/Budget Officer' },
  { value: 'compliance', label: 'Compliance Officer' },
  { value: 'grant-admin', label: 'Grant Administrator' },
  { value: 'technology', label: 'Technology/IT Officer' },
  { value: 'field-ops', label: 'Field Operations' },
  { value: 'other', label: 'Other (please specify)' },
];

const organizationTypes = [
  {
    value: 'small-medium-town',
    label: 'Small – Medium Town Department/Agency',
  },
  { value: 'small-city', label: 'Small City Department/Agency' },
  { value: 'medium-city', label: 'Medium City Department/Agency' },
  { value: 'large-city', label: 'Large City Department/Agency' },
  { value: 'state', label: 'State Agency' },
  { value: 'real-estate-developer', label: 'Real Estate Developer' },
  { value: 'community-dev', label: 'Community Development Corporation' },
  { value: 'housing-authority', label: 'Housing Authority' },
  { value: 'real-estate-consultant', label: 'Real-Estate Consultant' },
  { value: 'real-estate-service', label: 'Real-Estate Service Provider' },
  { value: 'nonprofit-housing', label: 'Non-Profit Housing Organization' },
  { value: 'other', label: 'Other (please specify)' },
];

const challenges = [
  {
    id: 'multiple-programs',
    label: 'Managing multiple programs across different systems',
  },
  {
    id: 'poor-communication',
    label: 'Poor communication between stakeholders',
  },
  {
    id: 'tracking-budgets',
    label: 'Tracking project budgets and expenditures in real-time',
  },
  {
    id: 'track-outcomes',
    label: 'Limited ability to track program outcomes/impact',
  },
  {
    id: 'visibility',
    label: 'Lack of visibility into project status and timelines',
  },
  {
    id: 'compliance-docs',
    label: 'Difficulty maintaining compliance documentation',
  },
  { id: 'manual-data', label: 'Manual data entry and reporting processes' },
  {
    id: 'inconsistent-execution',
    label: 'Inconsistent project execution across teams',
  },
  {
    id: 'intake-bottlenecks',
    label: 'Application intake and processing bottlenecks',
  },
  {
    id: 'vendor-management',
    label: 'Vendor management and coordination challenges',
  },
];

const steps = [
  { id: 1, label: 'Organization Info' },
  { id: 2, label: 'Solutions' },
  { id: 3, label: 'Schedule Demo' },
];

interface FormData {
  name: string;
  primaryRole: string;
  primaryRoleOther?: string;
  organizationType: string;
  organizationTypeOther?: string;
  selectedChallenges: string[];
  otherChallenges?: string;
  eventTitle?: string;
  eventDate?: string;
  eventTime?: string;
  timeZone?: string;
  email: string;
}

const FormContent: React.FC = () => {
  const {
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<FormData>();
  const selectedChallenges = watch('selectedChallenges') || [];
  const otherChallenges = watch('otherChallenges') || '';
  const primaryRole = watch('primaryRole');
  const organizationType = watch('organizationType');

  const toggleChallenge = (challengeId: string) => {
    const newChallenges = selectedChallenges.includes(challengeId)
      ? selectedChallenges.filter((id: string) => id !== challengeId)
      : [...selectedChallenges, challengeId];
    setValue('selectedChallenges', newChallenges, { shouldValidate: true });
  };

  return (
    <div className='space-y-6'>
      <div className='space-y-2'>
        <label className='block text-sm font-medium text-gray-700'>Name</label>
        <div className='w-full'>
          <HookInput name='name' type='text' required customClassName='w-full' />
        </div>
      </div>

      <div className='space-y-2'>
        <label className='block text-sm font-medium text-gray-700'>Email</label>
        <div className='w-full'>
          <HookInput name='email' type='email' required customClassName='w-full' />
        </div>
      </div>

      <div className='space-y-2'>
        <label className='block text-sm font-medium text-gray-700'>What is your primary role in your organization?</label>
        <div className='w-full'>
          <HookSelect name='primaryRole' required customClassName='w-full' options={organizationRoles} />
          {primaryRole === 'other' && (
            <div className='mt-2'>
              <HookInput name='primaryRoleOther' type='text' required customClassName='w-full' placeholder='Please specify your role' />
            </div>
          )}
        </div>
      </div>

      <div className='space-y-2'>
        <label className='block text-sm font-medium text-gray-700'>Which best describes your organization?</label>
        <div className='w-full'>
          <HookSelect name='organizationType' required customClassName='w-full' options={organizationTypes} />
          {organizationType === 'other' && (
            <div className='mt-2'>
              <HookInput
                name='organizationTypeOther'
                type='text'
                required
                customClassName='w-full'
                placeholder='Please specify your organization type'
              />
            </div>
          )}
        </div>
      </div>

      <div className='space-y-3'>
        <label className='block text-sm font-medium text-gray-700'>
          Which challenges are you most hoping to address? (Select all that apply)
        </label>
        <div className={styles['tag-container']}>
          {challenges.map((challenge) => (
            <button
              key={challenge.id}
              type='button'
              onClick={() => toggleChallenge(challenge.id)}
              className={`${styles.tag} ${selectedChallenges.includes(challenge.id) ? styles['tag-selected'] : ''}`}
            >
              <span className='text-center'>{challenge.label}</span>
            </button>
          ))}
        </div>
        {errors.selectedChallenges && <p className='text-sm text-red-600 mt-1'>{errors.selectedChallenges.message}</p>}
      </div>

      <div className='space-y-2'>
        <label className='block text-sm font-medium text-gray-700'>
          Please list any specific issues you are having that we have not captured above?
        </label>
        <textarea
          value={otherChallenges}
          onChange={(e) => setValue('otherChallenges', e.target.value)}
          rows={4}
          className='w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-hidden focus:ring-2 focus:ring-green-500 focus:border-green-500'
        />
      </div>

      <div className='flex justify-end space-x-4 mt-8'>
        <Button
          type='submit'
          btnStyle='primary'
          btnSize='lg'
          label='Next'
          customClassName='px-6 py-2 transition-all duration-200 hover:bg-green-600 focus:ring-2 focus:ring-green-500'
        />
      </div>
    </div>
  );
};

const OrganizationInfo = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData | null>(null);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [_isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleNext = () => {
    setCompletedSteps((prev) => [...new Set([...prev, currentStep])]);
    setCurrentStep((prev) => Math.min(prev + 1, steps.length));
  };

  const onSubmitForm = async (data: FormData) => {
    if (currentStep === 1) {
      try {
        setIsSubmitting(true);
        setError(null);

        await storeOrganizationInfo({
          name: data.name,
          email: data.email,
          primaryRole: data.primaryRole,
          organizationType: data.organizationType,
          selectedChallenges: data.selectedChallenges,
          otherChallenges: data.otherChallenges,
        });

        // Store the form data in localStorage for later use
        Object.entries(data).forEach(([key, value]) => {
          localStorage.setItem(key, typeof value === 'string' ? value : JSON.stringify(value));
        });

        setFormData(data);
        handleNext();
      } catch (err) {
        setError('Failed to save organization information. Please try again.');
        console.error('Error saving organization info:', err);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // Handle other form submissions (e.g., scheduling)
      Object.entries(data).forEach(([key, value]) => {
        localStorage.setItem(key, typeof value === 'string' ? value : JSON.stringify(value));
      });
      setFormData(data);
      handleNext();
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className='bg-white shadow-sm rounded-lg'>
            <div className='p-4 sm:p-6'>
              <h2 className='text-xl font-medium text-green-600 mb-6'>Organization Information</h2>

              {error && (
                <div className='mb-4 p-4 bg-red-50 border border-red-200 rounded-md'>
                  <p className='text-sm text-red-600'>{error}</p>
                </div>
              )}

              <HookFormProvider
                defaultValues={{
                  name: formData?.name || '',
                  email: formData?.email || '',
                  primaryRole: formData?.primaryRole || '',
                  organizationType: formData?.organizationType || '',
                  selectedChallenges: formData?.selectedChallenges || [],
                  otherChallenges: formData?.otherChallenges || '',
                }}
                resolver={yupResolver(validationSchema[1]) as any}
              >
                <HookForm<FormData> onSubmit={onSubmitForm}>
                  <FormContent />
                </HookForm>
              </HookFormProvider>
            </div>
          </div>
        );
      case 2:
        return <SENTerminalSolutions onNext={handleNext} currentStep={currentStep} />;
      case 3:
        return (
          <HookFormProvider
            defaultValues={{
              eventTitle: formData?.eventTitle || 'Product Demo',
              eventDate: formData?.eventDate || '',
              eventTime: formData?.eventTime || '',
              timeZone: formData?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone,
            }}
            resolver={yupResolver(validationSchema[2]) as any}
          >
            <HookForm onSubmit={onSubmitForm}>
              <ScheduleDemo onNext={handleNext} currentStep={currentStep} />
            </HookForm>
          </HookFormProvider>
        );
      default:
        return null;
    }
  };

  return (
    <div className='relative flex flex-col lg:flex-row min-h-screen bg-gray-50'>
      {/* Mobile Stepper */}
      <div className='lg:hidden w-full'>
        <div className='flex items-center justify-between px-4 py-2 bg-white border-b'>
          <button onClick={() => navigate('/')} className='text-gray-600 hover:text-gray-900'>
            Back to Home
          </button>
        </div>
        <div className={styles.stepper}>
          {steps.map((step, index) => (
            <div
              key={step.id}
              className={styles.step}
              style={{
                cursor: 'default',
                opacity: currentStep === step.id ? 1 : completedSteps.includes(step.id) ? 0.7 : 0.5,
              }}
            >
              <div className={styles.stepCircleContainer}>
                <div
                  className={`${styles.stepCircle} ${
                    currentStep === step.id ? styles.stepCircleActive : completedSteps.includes(step.id) ? styles.stepCircleCompleted : ''
                  }`}
                >
                  {completedSteps.includes(step.id) ? '✓' : step.id}
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`${styles.stepperLine} ${
                      currentStep > step.id || completedSteps.includes(step.id + 1) ? styles.stepperLineActive : ''
                    }`}
                  />
                )}
              </div>
              <div
                className={`${styles.stepLabel} ${
                  currentStep === step.id ? styles.stepLabelActive : completedSteps.includes(step.id) ? styles.stepLabelCompleted : ''
                }`}
              >
                {step.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Desktop Sidebar */}
      <div className={`${styles.sidebar} bg-white border-r border-gray-200`}>
        <div className='p-6'>
          <div className='hidden lg:flex items-center justify-between mb-8'>
            <div className='flex items-center'>
              <span className='text-3xl font-bold text-green-600'>S</span>
              <span className='text-3xl font-bold text-gray-600 ml-1'>T P</span>
            </div>
            <button onClick={() => navigate('/')} className='text-gray-600 hover:text-gray-900 text-sm'>
              Back to Home
            </button>
          </div>

          <nav className='space-y-1'>
            {steps.map((step) => (
              <button
                key={step.id}
                disabled={true}
                className={`w-full text-left px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  currentStep === step.id
                    ? 'text-white bg-green-500'
                    : completedSteps.includes(step.id)
                      ? 'text-gray-500 bg-gray-50'
                      : 'text-gray-400 bg-gray-50 opacity-50'
                }`}
              >
                <div className='flex items-center'>
                  <span className='mr-2'>{completedSteps.includes(step.id) ? '✓' : step.id}</span>
                  {step.label}
                </div>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className='flex-1'>
        <div className='max-w-4xl mx-auto py-4 sm:py-8 px-4 sm:px-6 lg:px-8'>{renderStep()}</div>
      </div>
    </div>
  );
};

export default OrganizationInfo;
